import styled, { css, keyframes } from 'styled-components'
import {
	backgroundOrange,
	black,
	blackOrange,
	blue,
	blueDark,
	darkGrey,
	darkOrange,
	grey,
	orange,
	red,
	success,
	textMuted,
	vanilla,
	white,
} from '@/theme/colors'

const border1 = 'rgba(38, 38, 38, 0.08)'
const border2 = 'rgba(38, 38, 38, 0.14)'

const fadeSlideUp = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: translateY(0); }
`

const pulseDot = keyframes`
	0% { opacity: 1; }
	100% { opacity: 0.55; }
`

const orbitSpin = keyframes`
	from { transform: rotate(0deg); }
	to   { transform: rotate(360deg); }
`

const orbitSpinReverse = keyframes`
	from { transform: rotate(360deg); }
	to   { transform: rotate(0deg); }
`

const stagger = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
`

export const PageContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	background: ${backgroundOrange};
	overflow: auto;
	padding-bottom: 32px;
`

export const HeroRow = styled.div`
	max-width: 1400px;
	width: 100%;
	margin: 0 auto;
	padding: 24px 24px 4px;

	@media (max-width: 600px) {
		padding: 16px 14px 4px;
	}
`

export const HeroTitle = styled.h1`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 900;
	font-size: 40px;
	line-height: 1.05;
	letter-spacing: -0.02em;
	color: ${blue};
	margin: 0;

	@media (max-width: 600px) {
		font-size: 26px;
	}
`

export const HeroSubtitle = styled.div`
	font-size: 16px;
	color: ${textMuted};
	margin-top: 6px;

	@media (max-width: 600px) {
		font-size: 14px;
	}
`

export const ContentArea = styled.div`
	flex: 1;
	max-width: 1400px;
	width: 100%;
	margin: 0 auto;
	padding: 16px 24px 24px;

	@media (max-width: 600px) {
		padding: 12px 14px 18px;
	}
`

export const ContentCard = styled.div`
	width: 100%;
	background: ${white};
	border-radius: 16px;
	border: 1px solid ${border1};
	box-shadow: 0 4px 12px rgba(84, 18, 2, 0.08), 0 1px 2px rgba(38, 18, 2, 0.04);
	overflow: hidden;
	animation: ${fadeSlideUp} 200ms ease-out;
	display: flex;
	flex-direction: column;
`

export const CardBody = styled.div`
	padding: 20px 24px 4px;
	flex: 1;
	min-height: 0;

	@media (max-width: 600px) {
		padding: 16px 14px 4px;
	}
`

export const CardBodyCentered = styled.div`
	padding: 36px 28px 16px;
	text-align: center;
	flex: 1;
	min-height: 0;

	@media (max-width: 600px) {
		padding: 24px 16px 12px;
	}
`

export const CardFooter = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid ${border1};
	background: ${vanilla};
	flex-wrap: wrap;

	@media (max-width: 600px) {
		padding: 12px 14px;
	}
`

export const FooterHint = styled.div`
	font-size: 12px;
	color: ${textMuted};
	flex: 1;
	min-width: 200px;

	@media (max-width: 600px) {
		min-width: 0;
		flex-basis: 100%;
	}
`

export const FooterActions = styled.div`
	display: flex;
	gap: 10px;
	flex-shrink: 0;
`

export const PrimaryButton = styled.button<{ $variant?: 'cool' | 'warm'; $disabled?: boolean }>`
	background: ${({ $variant }) => ($variant === 'warm' ? orange : blue)};
	color: ${white};
	border: none;
	border-radius: 14px;
	padding: 10px 20px;
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	transition: background 0.15s ease, transform 0.12s ease;

	&:hover {
		background: ${({ $variant }) => ($variant === 'warm' ? darkOrange : blueDark)};
	}

	&:active {
		transform: scale(0.97);
	}

	${({ $disabled }) =>
		$disabled &&
		css`
			opacity: 0.45;
			cursor: not-allowed;
			&:hover {
				background: ${blue};
			}
		`}
`

export const GhostButton = styled.button`
	background: transparent;
	color: ${black};
	border: 1px solid ${border2};
	border-radius: 14px;
	padding: 10px 20px;
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	cursor: pointer;
	transition: background 0.15s ease, transform 0.12s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.04);
	}

	&:active {
		transform: scale(0.97);
	}
`

/* ─── Trip context strip ─── */

export const TripStrip = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: rgba(57, 64, 128, 0.06);
	border-radius: 12px;
	margin-bottom: 16px;
	flex-wrap: wrap;
`

export const TripStripIcon = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: ${blue};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

export const TripStripLabel = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 11px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: ${orange};
`

export const TripStripName = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	color: ${black};
	margin-top: 2px;
`

export const TripStripDates = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	font-size: 12px;

	span:first-child {
		font-weight: 700;
		color: ${black};
	}
	span:last-child {
		color: ${textMuted};
	}
`

/* ─── Drop zone ─── */

export const DropZoneRoot = styled.div<{ $state: 'idle' | 'dragOver' }>`
	position: relative;
	border-radius: 16px;
	background: ${backgroundOrange};
	padding: 36px 28px 24px;
	text-align: center;
	overflow: hidden;
	transition: all 200ms ease;
	min-height: 240px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	@media (max-width: 600px) {
		padding: 24px 14px 18px;
		min-height: 200px;
	}

	${({ $state }) =>
		$state === 'idle'
			? css`
					border: 1.5px dashed ${border2};
				`
			: css`
					background: rgba(209, 95, 42, 0.06);
					border: 2px dashed ${orange};
					box-shadow: 0 8px 24px rgba(209, 95, 42, 0.2);
				`}
`

export const DropIconWrap = styled.div<{ $active: boolean }>`
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 12px;
	background: ${({ $active }) => ($active ? orange : 'rgba(209,95,42,0.10)')};
	color: ${({ $active }) => ($active ? white : darkOrange)};
	transition: all 200ms ease;
`

export const DropTitle = styled.div<{ $active?: boolean }>`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 18px;
	margin-bottom: 6px;
	color: ${({ $active }) => ($active ? darkOrange : black)};
`

export const DropHint = styled.div`
	font-size: 14px;
	color: ${textMuted};
	max-width: 380px;
	margin: 0 auto 16px;
`

export const BrowseButton = styled.button`
	background: transparent;
	color: ${orange};
	border: none;
	font-family: inherit;
	font-weight: 700;
	font-size: inherit;
	cursor: pointer;
	text-decoration: underline;
	padding: 0;

	&:hover {
		color: ${darkOrange};
	}
`

export const DropLimits = styled.div`
	font-size: 12px;
	color: ${darkGrey};
`

/* ─── File list panel ─── */

export const FileListPanel = styled.div`
	background: ${white};
	border-radius: 14px;
	border: 1px solid ${border1};
	padding: 14px;
	margin-bottom: 12px;
`

export const FileListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${darkOrange};
	margin-bottom: 10px;

	span:last-child {
		color: ${darkGrey};
		font-weight: 500;
	}
`

export const FileRowRoot = styled.div<{ $error?: boolean; $kind: 'pdf' | 'image' }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid;
	margin-bottom: 8px;

	${({ $error, $kind }) =>
		$error
			? css`
					background: rgba(243, 111, 111, 0.1);
					border-color: rgba(243, 111, 111, 0.45);
				`
			: $kind === 'image'
				? css`
						background: rgba(57, 64, 128, 0.06);
						border-color: rgba(57, 64, 128, 0.18);
					`
				: css`
						background: rgba(209, 95, 42, 0.06);
						border-color: rgba(209, 95, 42, 0.2);
					`}
`

export const FileIconBox = styled.div<{ $error?: boolean; $kind: 'pdf' | 'image' }>`
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${({ $error, $kind }) =>
		$error ? '#A83B3B' : $kind === 'image' ? blue : darkOrange};
`

export const FileInfo = styled.div`
	flex: 1;
	min-width: 0;
`

export const FileName = styled.div<{ $error?: boolean }>`
	font-size: 14px;
	font-weight: 600;
	color: ${({ $error }) => ($error ? red : black)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const FileMeta = styled.div`
	font-size: 12px;
	color: ${textMuted};
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 2px;
	flex-wrap: wrap;
`

export const FileMetaDot = styled.span`
	color: ${darkGrey};
`

export const FileMetaError = styled.span`
	color: ${red};
	font-weight: 600;
`

export const FileMetaReady = styled.span`
	color: ${success};
	font-weight: 600;
`

export const FileRemoveButton = styled.button`
	background: transparent;
	border: none;
	color: ${textMuted};
	width: 28px;
	height: 28px;
	border-radius: 6px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background: rgba(0, 0, 0, 0.04);
		color: ${black};
	}
`

export const AddMoreButton = styled.button`
	width: 100%;
	background: transparent;
	border: 1px dashed ${border2};
	color: ${textMuted};
	padding: 10px;
	border-radius: 10px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	margin-top: 4px;
	font-family: inherit;

	&:hover {
		background: rgba(0, 0, 0, 0.02);
		color: ${black};
	}
`

/* ─── Notes ─── */

export const NotesWrap = styled.div`
	margin-top: 16px;
	padding-bottom: 16px;
`

export const NotesLabel = styled.label`
	display: block;
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 12px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${textMuted};
	margin-bottom: 8px;
`

export const NotesArea = styled.textarea`
	width: 100%;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: 12px;
	padding: 12px 14px;
	font-family: inherit;
	font-size: 14px;
	color: ${black};
	resize: vertical;
	outline: none;
	box-sizing: border-box;
	line-height: 1.5;
	min-height: 60px;

	&:focus {
		border-color: ${orange};
	}
`

/* ─── Validation alert banner ─── */

export const AlertBanner = styled.div`
	background: rgba(243, 111, 111, 0.08);
	border: 1px solid rgba(243, 111, 111, 0.35);
	border-radius: 12px;
	padding: 12px 14px;
	margin-bottom: 14px;
	display: flex;
	align-items: flex-start;
	gap: 12px;
`

export const AlertIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: ${red};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

export const AlertTitle = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	color: ${black};
`

export const AlertSub = styled.div`
	font-size: 13px;
	color: ${textMuted};
	margin-top: 2px;
`

/* ─── Status plates (no_events, failed, rate_limit, stuck) ─── */

export const Plate = styled.div<{ $tone: 'orange' | 'red' | 'blue' | 'gold' }>`
	width: 88px;
	height: 88px;
	border-radius: 50%;
	margin: 0 auto 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ $tone }) =>
		$tone === 'red' ? red : $tone === 'blue' ? blue : darkOrange};
	background: ${({ $tone }) =>
		$tone === 'red'
			? 'rgba(243,111,111,0.14)'
			: $tone === 'blue'
				? 'rgba(57,64,128,0.10)'
				: $tone === 'gold'
					? 'rgba(237,146,71,0.18)' /* ${gold} at 18% */
					: 'rgba(209,95,42,0.10)'};
`

export const StateHeadline = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 22px;
	font-weight: 700;
	color: ${blue};
	margin-bottom: 8px;
	letter-spacing: -0.01em;
`

export const StateParagraph = styled.div`
	font-size: 15px;
	color: ${textMuted};
	max-width: 440px;
	margin: 0 auto 18px;
	line-height: 1.5;
`

export const InlinePanel = styled.div`
	text-align: left;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: 12px;
	padding: 12px 16px;
	max-width: 440px;
	margin: 0 auto 20px;
`

export const InlinePanelLabel = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: ${orange};
	margin-bottom: 8px;
`

export const InlinePanelMutedLabel = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: ${darkGrey};
	margin-bottom: 6px;
`

export const ErrorRefBlock = styled.div`
	font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	color: ${textMuted};
`

export const TipsList = styled.ul`
	margin: 0;
	padding-left: 18px;
	font-size: 13px;
	color: ${black};
	line-height: 1.7;
`

/* ─── Rate limit gauge ─── */

export const RateLimitPanel = styled.div`
	background: ${white};
	border: 1px solid ${border1};
	border-radius: 12px;
	padding: 14px 18px;
	max-width: 420px;
	margin: 0 auto 12px;
	display: flex;
	align-items: center;
	gap: 14px;
	text-align: left;
`

export const GaugeColumn = styled.div`
	flex: 1;
`

export const GaugeLabel = styled.div`
	font-size: 13px;
	color: #5c5c5c;
	margin-bottom: 6px;
`

export const GaugeTrack = styled.div`
	height: 8px;
	border-radius: 4px;
	background: ${grey};
	overflow: hidden;
	position: relative;
`

export const GaugeFill = styled.div<{ $pct: number }>`
	height: 100%;
	width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
	background: linear-gradient(90deg, ${orange} 0%, ${darkOrange} 100%);
	border-radius: 4px;
`

export const GaugeCount = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 900;
	font-size: 22px;
	color: ${blue};
	min-width: 60px;
	text-align: right;
`

/* ─── Processing state ─── */

export const ProcessingWrap = styled.div`
	padding: 32px 24px 16px;
	text-align: center;
`

export const ProcessingHeading = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 22px;
	font-weight: 700;
	color: ${blue};
	letter-spacing: -0.01em;
	margin-bottom: 8px;
`

export const ProcessingSub = styled.div`
	font-size: 14px;
	color: ${textMuted};
	margin: 0 auto 24px;
	max-width: 380px;
`

export const StepsList = styled.div`
	text-align: left;
	max-width: 420px;
	margin: 0 auto;
`

export const StepRow = styled.div<{ $opacity: number }>`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 10px 0;
	opacity: ${({ $opacity }) => $opacity};
	transition: opacity 200ms;
`

export const StepBullet = styled.div<{ $state: 'done' | 'active' | 'pending' }>`
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background: ${({ $state }) =>
		$state === 'done' ? success : $state === 'active' ? orange : grey};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-top: 1px;
`

export const StepActiveDot = styled.div`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${white};
	animation: ${pulseDot} 0.6s ease-in-out infinite alternate;
`

export const StepLabel = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	color: ${black};
`

export const StepDetail = styled.div`
	font-size: 13px;
	color: ${textMuted};
	margin-top: 1px;
`

/* ─── Orbit Loader ─── */

export const LoaderWrap = styled.div<{ $size: number }>`
	position: relative;
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	margin: 0 auto 22px;
`

export const LoaderRingOuter = styled.div`
	position: absolute;
	inset: 0;
	border: 2px dashed rgba(209, 95, 42, 0.4);
	border-radius: 50%;
	animation: ${orbitSpin} 6s linear infinite;
`

export const LoaderRingInner = styled.div`
	position: absolute;
	inset: 14%;
	border: 1.5px dashed rgba(57, 64, 128, 0.3);
	border-radius: 50%;
	animation: ${orbitSpinReverse} 9s linear infinite;
`

export const LoaderCore = styled.div<{ $size: number }>`
	position: absolute;
	left: 50%;
	top: 50%;
	width: ${({ $size }) => $size * 0.4}px;
	height: ${({ $size }) => $size * 0.4}px;
	margin-left: ${({ $size }) => -($size * 0.2)}px;
	margin-top: ${({ $size }) => -($size * 0.2)}px;
	border-radius: 50%;
	background: ${orange};
	box-shadow: 0 8px 22px rgba(208, 95, 42, 0.45),
		inset -4px -4px 12px rgba(0, 0, 0, 0.18);
`

export const LoaderSatellite = styled.div<{ $size: number }>`
	position: absolute;
	left: 50%;
	top: 0;
	width: 12px;
	height: 12px;
	margin-left: -6px;
	border-radius: 50%;
	background: ${blue};
	transform-origin: 6px ${({ $size }) => $size / 2}px;
	animation: ${orbitSpin} 3s linear infinite;
	box-shadow: 0 4px 12px rgba(57, 64, 128, 0.45);
`

/* ─── Success ─── */

export const SuccessBanner = styled.div`
	background: linear-gradient(135deg, rgba(57, 64, 128, 0.08), rgba(209, 95, 42, 0.08));
	border: 1px solid rgba(57, 64, 128, 0.12);
	border-radius: 14px;
	padding: 16px 18px;
	display: flex;
	align-items: center;
	gap: 14px;
	margin-bottom: 18px;
`

export const SuccessCheckChip = styled.div`
	width: 44px;
	height: 44px;
	border-radius: 12px;
	background: ${success};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${white};
`

export const SuccessHeadline = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 18px;
	color: ${blue};
`

export const SuccessSub = styled.div`
	font-size: 13px;
	color: ${textMuted};
	margin-top: 2px;
`

export const SuccessBannerText = styled.div`
	flex: 1;
	min-width: 0;
`

export const EventListPanel = styled.div`
	background: ${white};
	border-radius: 14px;
	border: 1px solid ${border1};
	overflow: hidden;
`

export const DayGroup = styled.div<{ $first: boolean }>`
	border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${border1}`)};
`

export const DayHeader = styled.div`
	padding: 10px 16px;
	background: ${vanilla};
	font-size: 12px;
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${blackOrange};
`

export const EventRow = styled.div<{ $first: boolean; $idx: number }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${border1}`)};
	animation: ${stagger} 200ms ease-out backwards;
	animation-delay: ${({ $idx }) => $idx * 30}ms;
`

export const CategoryPill = styled.div<{ $color: string }>`
	background: ${({ $color }) => $color};
	color: ${white};
	font-family: 'Lato', system-ui, sans-serif;
	font-size: 10px;
	font-weight: 700;
	padding: 3px 8px;
	border-radius: 999px;
	white-space: nowrap;
	flex-shrink: 0;
	/* Uniform width so event names line up regardless of category label length. */
	min-width: 108px;
	text-align: center;
`

export const EventInfo = styled.div`
	flex: 1;
	min-width: 0;
`

export const EventName = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 14px;
	color: ${black};
`

export const EventLocation = styled.div`
	font-size: 12px;
	color: ${textMuted};
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 2px;

	svg {
		color: ${darkOrange};
		flex-shrink: 0;
	}
`

export const EventNoLocation = styled.div`
	font-size: 12px;
	color: ${darkGrey};
	margin-top: 2px;
`

export const EventTime = styled.div`
	font-size: 13px;
	color: ${textMuted};
	min-width: 50px;
	text-align: right;
`

export const EventDeleteButton = styled.button`
	border: none;
	background: transparent;
	color: ${darkGrey};
	width: 28px;
	height: 28px;
	border-radius: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	flex-shrink: 0;
	transition: background 0.15s ease, color 0.15s ease;

	&:hover:not(:disabled) {
		background: rgba(243, 111, 111, 0.12);
		color: ${red};
	}

	&:disabled {
		opacity: 0.4;
		cursor: default;
	}
`

export const RemoveAllButton = styled.button`
	border: none;
	background: transparent;
	color: ${red};
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 12px;
	cursor: pointer;
	padding: 6px 8px;
	border-radius: 8px;
	flex-shrink: 0;
	white-space: nowrap;
	transition: background 0.15s ease;

	&:hover:not(:disabled) {
		background: rgba(243, 111, 111, 0.1);
	}

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
`

export const SmallGhostButton = styled.button`
	border: none;
	background: transparent;
	color: ${textMuted};
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 12px;
	cursor: pointer;
	padding: 6px 8px;
	border-radius: 8px;
	white-space: nowrap;
	transition: background 0.15s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.05);
	}
`

export const RemoveAllConfirm = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
`

export const HiddenInput = styled.input`
	display: none;
`

export const StrongAccent = styled.strong`
	color: ${black};
`

